import React, { useState, useEffect } from 'react'; // Добавили useEffect сюда
import { Phone, Mail, Clock, Send } from 'lucide-react';
import { Button, Input, Textarea } from '../components/ui/Primitives';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

//const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = "/api";

// === ДАННЫЕ ===
const contactData = {
  title: 'КОНТАКТИ',
  subtitle: "Зв'яжіться з нами",
  description: 'Готові розпочати співпрацю або дізнатися більше? Напишіть нам — відповідаємо протягом 1 години.',
  info: [
    {
      label: 'Telegram',
      value: '@bmg_managerrrr',
      icon: 'send',
      href: 'https://t.me/bmg_managerrrr'
    },
    {
      label: 'Телефон',
      value: '+38 (063) 412-04-90',
      icon: 'phone',
      href: 'tel:+380634120490'
    },
    {
      label: 'Email',
      value: 'info@bmgroup.ua',
      icon: 'mail',
      href: 'mailto:info@bmgroup.ua'
    },
    {
      label: 'Години роботи',
      value: 'Пн-Пт: 10:00 - 19:00',
      icon: 'clock',
      href: null
    }
  ],
  socials: [
    { name: 'Telegram', url: 'https://t.me/bmg_managerrrr', icon: 'send' }
  ]
};

const iconMap = {
  phone: Phone,
  mail: Mail,
  clock: Clock,
  send: Send
};

const validators = {
  name:    (v) => v.trim().length < 2 ? "Введіть ім'я (мінімум 2 символи)" : '',
  email:   (v) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? 'Введіть коректний email' : '',
  phone:   (v) => v.replace(/\D/g, '').length < 10 ? 'Введіть коректний номер телефону' : '',
  message: (v) => v.trim().length < 5 ? 'Повідомлення занадто коротке' : '',
};

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: validators[name](value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validators[name](value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // валідація всіх полів перед відправкою
    const allTouched = { name: true, email: true, phone: true, message: true };
    const allErrors = Object.fromEntries(
      Object.entries(validators).map(([k, fn]) => [k, fn(formData[k])])
    );
    setTouched(allTouched);
    setErrors(allErrors);
    if (Object.values(allErrors).some(Boolean)) return;
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast({
          title: 'Заявка надіслана!',
          description: 'Ми зв\'яжемось з вами найближчим часом.',
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: 'Помилка',
        description: 'Не вдалося надіслати заявку. Спробуйте ще раз.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-black min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {contactData.title}
            </h1>
            <p className="text-2xl text-emerald-500 mb-8">
              {contactData.subtitle}
            </p>
            <p className="text-gray-400 text-lg leading-relaxed">
              {contactData.description}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-2xl">
              <h2 className="text-2xl font-bold text-white mb-6">Зв'яжіться з нами</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="text-gray-300 text-sm mb-2 block">Ім'я *</label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`bg-white/5 text-white placeholder:text-gray-500 focus:border-emerald-500 ${errors.name && touched.name ? 'border-red-500' : 'border-white/10'}`}
                    placeholder="Ваше ім'я"
                  />
                  {errors.name && touched.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="text-gray-300 text-sm mb-2 block">Email *</label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`bg-white/5 text-white placeholder:text-gray-500 focus:border-emerald-500 ${errors.email && touched.email ? 'border-red-500' : 'border-white/10'}`}
                    placeholder="your@email.com"
                  />
                  {errors.email && touched.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="text-gray-300 text-sm mb-2 block">Телефон *</label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`bg-white/5 text-white placeholder:text-gray-500 focus:border-emerald-500 ${errors.phone && touched.phone ? 'border-red-500' : 'border-white/10'}`}
                    placeholder="+380 (XX) XXX-XX-XX"
                  />
                  {errors.phone && touched.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <label className="text-gray-300 text-sm mb-2 block">Повідомлення *</label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows={5}
                    className={`bg-white/5 text-white placeholder:text-gray-500 focus:border-emerald-500 resize-none ${errors.message && touched.message ? 'border-red-500' : 'border-white/10'}`}
                    placeholder="Розкажіть нам про себе..."
                  />
                  {errors.message && touched.message && <p className="text-red-400 text-xs mt-1">{errors.message}</p>}
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-medium py-6 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'НАДСИЛАННЯ...' : 'НАДІСЛАТИ ЗАЯВКУ'}
                </Button>
                <p className="text-gray-500 text-xs text-center mt-3 flex items-center justify-center gap-1.5">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  Ваші дані захищені та не передаються третім особам
                </p>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-2xl">
                <h3 className="text-xl font-bold text-white mb-6">Контактна інформація</h3>
                <div className="space-y-6">
                  {contactData.info.map((item, index) => {
                    const Icon = iconMap[item.icon];
                    return (
                      <div key={index} className="flex items-start space-x-4">
                        <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="text-emerald-500" size={20} />
                        </div>
                        <div>
                          <p className="text-gray-400 text-sm">{item.label}</p>
                          {item.href ? (
                            <a
                              href={item.href}
                              target={item.href.startsWith('http') ? '_blank' : undefined}
                              rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                              className="text-white font-medium hover:text-emerald-400 transition-colors"
                            >
                              {item.value}
                            </a>
                          ) : (
                            <p className="text-white font-medium">{item.value}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-emerald-500/10 border border-emerald-500/20 px-6 py-4 rounded-xl flex items-center gap-3">
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
                <p className="text-emerald-400 text-sm font-medium">
                  Відповідаємо протягом 1 години у робочий час
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-2xl">
                <h3 className="text-xl font-bold text-white mb-6">Ми в соцмережах</h3>
                <div className="flex space-x-4">
                  {contactData.socials.map((social, index) => {
                    const Icon = iconMap[social.icon];
                    return (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={social.name}
                        className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center hover:bg-emerald-500/20 transition-colors group"
                      >
                        <Icon className="text-emerald-500 group-hover:scale-110 transition-transform" size={20} />
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;